/*
 *   File : competition-overview.js
 *   Author : https://evoqins.com
 *   Description : Competition detail page
 *   Integrations : react-toastify
 *   Version : 1.0.0
*/

// Package
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";

// Custom components
import { Loader } from "../../../Component/Others";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { DownloadButton } from "../../../Component/Buttons";
import { DataTableComponent } from "../../../Component/DataTable";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";
import { _isUaeRegion } from "../../../Helper/country";

const ITEMS_PER_PAGE = 20;

const CompetitionDetail = () => {

    const scrollRef = useRef(null);

    const navigate = useNavigate();
    const { state } = useLocation();

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const [leaderBoardData, setLeaderBoardData] = useState({});
    const [leaderBoardLoading, setLeaderBoardLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [downloadLoader, setDownloadLoader] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);


    useEffect(() => {
        if (state != null) {
            _leaderBoardData();
        }
    }, [currentPage, state]);

    // handle paginate store list 
    const _handlePageChange = (page_num) => {
        setCurrentPage(page_num);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // API - Get leader board data
    const _leaderBoardData = (page_num = currentPage) => {
        setLeaderBoardLoading(true);

        let is_post = true;
        let url = 'CA/competition/leaderboard';
        let data = {
            "competition_id": state?.id || null,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setLeaderBoardData(response.data);
            } else {
                setLeaderBoardData({});
            }
            setLeaderBoardLoading(false);
        });
    };

    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/competition/leaderboard/download";
        let data = {
            "competition_id": state?.id || null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    const TABLE_COLUMNS = [
        {
            name: 'Participant name',
            selector: row => row.name || "-",
            cell: row => <>{row.name || "-"}</>,
            width: "280px",
            sortable: true
        },
        {
            name: 'Sales unit',
            selector: row => row.sales_unit || "-",
            width: "120px",
            sortable: true
        },
        ...(!_isUaeRegion(PROFILE_DATA)
            ? [{
                name: 'Sales amount',
                selector: row => row.sales_amount || "-",
                width: "120px",
                sortable: true
            }]
            : []),
        {
            name: 'Points awarded',
            selector: row => row.points || "-",
            width: "150px",
            sortable: true
        },
        {
            name: 'Progress',
            selector: row => row.progress_text || "-",
            width: "100px",
            sortable: true
        },
        {
            name: 'Status',
            selector: row => row.status,
            cell: row => <>
                {row.status ?
                    <span className={`${row.status.toLowerCase() === "Completed" ? "e-text-philippine-green" : "e-text-brilliant-blue "}  e-font-weight-500`}>{row.status}</span>
                    : "-"}
            </>,
            width: "100px"
        },
    ];

    return (
        <Fragment>
            <section className='pb-5 '>
                <div className='e-bg-lavender-blush-light padding-12px-top padding-88px-bottom padding-52px-lr'>
                    <div className='d-flex justify-content-space-between flex-wrap align-items-center'>
                        <div className='e-display-flex w-max-content cursor-pointer'
                            onClick={() => navigate(-1)} >
                            <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                                draggable={false}
                                className='cursor-pointer'
                                alt='arrow'
                            />
                            <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                                Leaderboard Details
                            </h1>
                        </div>
                    </div>

                </div>
                <div className="padding-52px-lr margin-neg-56px-top">
                    <div className='e-bg-white e-admin-box-shadow py-3 e-border-radius-16 '>
                        <div className="d-flex align-items-center justify-content-end px-4">
                            <DownloadButton loader={downloadLoader}
                                disabled={leaderBoardLoading || Object.keys(leaderBoardData)?.length === 0 || leaderBoardData?.data?.length === 0}
                                onClick={_download} />
                        </div>

                        {leaderBoardLoading ?
                            <Loader className="h-40vh" />  //loader
                            :
                            <>
                                {Object.keys(leaderBoardData)?.length !== 0 && leaderBoardData?.data?.length !== 0 ?
                                    <div ref={scrollRef} className={`e-table  no-hover e-store-table`}>
                                        <DataTableComponent columns={TABLE_COLUMNS}
                                            pagination={true}
                                            data={leaderBoardData.data}
                                            paginationPerPage={ITEMS_PER_PAGE}
                                            paginationServer={true}
                                            paginationTotalRows={leaderBoardData.total_count}
                                            paginationDefaultPage={currentPage}
                                            onChangePage={_handlePageChange} />
                                    </div>
                                    :
                                    <div className='min-vh-100 py-5 d-flex flex-column justify-content-center'>
                                        <EmptyScreen message="No data found" />
                                    </div>
                                }
                            </>
                        }
                    </div>
                </div>
            </section>
        </Fragment>
    )
}

export default CompetitionDetail;